import { z } from 'zod';
import { userRoleSchema } from './enums.js';
import { navKeySchema } from './nav.js';

/** Request and response contracts for /auth (docs/spec.md §5). */

export const loginSchema = z.object({
  username: z.string().trim().min(1).max(64),
  // Only a lower bound is enforced at the boundary — rejecting a long password
  // as "invalid" would be absurd, and the hash is fixed width regardless.
  password: z.string().min(1).max(200),
});
export type LoginInput = z.infer<typeof loginSchema>;

/**
 * Fingerprint / face sign-in (WebAuthn, docs/spec.md §14 round 4, G1).
 *
 * The registration/authentication payloads themselves are passed through
 * untyped (`unknown`) — they are produced and consumed by
 * @simplewebauthn/browser and @simplewebauthn/server, which own their shape,
 * and re-declaring it here would only create a second definition to keep in
 * sync. What matters at this boundary is the challenge id pairing.
 */
export const passkeyRegisterSchema = z.object({
  challengeId: z.string().min(1).max(64),
  response: z.unknown(),
  /** How the device is shown in Settings, e.g. "ຄອມພິວເຕີຕ້ອນຮັບ". */
  label: z.string().trim().max(120).optional().or(z.literal('')),
});
export type PasskeyRegisterInput = z.infer<typeof passkeyRegisterSchema>;

export const passkeyLoginSchema = z.object({
  challengeId: z.string().min(1).max(64),
  response: z.unknown(),
});
export type PasskeyLoginInput = z.infer<typeof passkeyLoginSchema>;

/** One enrolled device, as listed in Settings. */
export interface PasskeyRow {
  id: string;
  label: string | null;
  deviceType: string | null;
  backedUp: boolean;
  createdAt: string;
  lastUsedAt: string | null;
}

export const authUserSchema = z.object({
  id: z.number().int(),
  username: z.string(),
  fullName: z.string(),
  role: userRoleSchema,
  /** This role's currently-visible sidebar items — see docs/spec.md §14 round 3 (E8). */
  visibleNavKeys: z.array(navKeySchema),
});
export type AuthUser = z.infer<typeof authUserSchema>;
